import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { mockVerticals } from '@/mocks/verticals';
import { mockLeads } from '@/mocks/leads';
import { mockOffers } from '@/mocks/offers';
import { mockPartners } from '@/mocks/partners';
import { mockProperties } from '@/mocks/properties';
import { mockFunnels } from '@/mocks/funnels';
import {
  ArrowUpRight,
  Layers,
  MapPin,
  Users,
  FileText,
  ChevronRight,
  Activity,
  TrendingUp,
  TrendingDown,
  Minus,
} from 'lucide-react';

export default function VerticalsPage() {
  const navigate = useNavigate();


  const verticalCards = useMemo(() => {
    return mockVerticals.map(v => {
      const verticalLeads = mockLeads.filter(l => l.vertical_id === v.id && !l.deleted_at);
      const monthLeads = verticalLeads.filter(l => {
        const d = new Date(l.created_at);
        const now = new Date();
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });
      const routed = monthLeads.filter(l => l.routing_status === 'assigned').length;
      const unmatched = monthLeads.filter(l => l.routing_status === 'unmatched').length;
      const signed = monthLeads.filter(l => l.status === 'signed' || l.status === 'completed').length;
      const conversionRate = monthLeads.length > 0 ? Math.round((signed / monthLeads.length) * 100) : 0;

      const properties = mockProperties.filter(p => p.vertical_id === v.id);
      const offers = mockOffers.filter(o => o.vertical_id === v.id && o.status === 'active');
      const partnerIds = new Set(offers.map(o => o.partner_id));
      const funnels = mockFunnels.filter(f => properties.some(p => p.id === f.property_id));

      // Build a simple sparkline from last 14 days
      const sparkline = Array.from({ length: 14 }, (_, i) => {
        const d = new Date();
        d.setDate(d.getDate() - (13 - i));
        return verticalLeads.filter(l => {
          const ld = new Date(l.created_at);
          return ld.toDateString() === d.toDateString();
        }).length;
      });
      const maxSpark = Math.max(...sparkline, 1);
      const sparkPoints = sparkline.map((v, i) => {
        const x = (i / 13) * 100;
        const y = 100 - (v / maxSpark) * 100;
        return `${x},${y}`;
      }).join(' ');

      return {
        ...v,
        monthTotal: monthLeads.length,
        routed,
        unmatched,
        signed,
        conversionRate,
        propertyCount: properties.length,
        offerCount: offers.length,
        partnerCount: partnerIds.size,
        funnelCount: funnels.length,
        sparkPoints,
      };
    });
  }, []);

  const TrendIcon = ({ val }: { val: number }) => {
    if (val > 0) return <TrendingUp size={12} className="inline mr-1" style={{ color: 'var(--success-text)' }} />;
    if (val < 0) return <TrendingDown size={12} className="inline mr-1" style={{ color: 'var(--danger-text)' }} />;
    return <Minus size={12} className="inline mr-1" style={{ color: 'var(--text-muted)' }} />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Verticals</h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            Lead categories, intake schemas, and partner pipelines
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium px-2.5 py-1 rounded-full border" style={{ borderColor: 'var(--border-primary)', color: 'var(--text-muted)' }}>
            {mockVerticals.length} categories
          </span>
        </div>
      </div>

      {/* Cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {verticalCards.map((v) => (
          <button
            key={v.id}
            onClick={() => navigate(`/verticals/${v.id}`)}
            className="group text-left w-full rounded-xl border p-5 transition-all duration-200 relative overflow-hidden hover-card"
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: v.active ? 'var(--accent-dim)' : 'var(--bg-input)' }}
                >
                  <Layers size={18} style={{ color: v.active ? 'var(--accent)' : 'var(--text-muted)' }} />
                </div>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>{v.name}</p>
                  <p className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                    {v.active ? (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: 'var(--accent)' }} />
                        Active
                      </span>
                    ) : 'Inactive'}
                  </p>
                </div>
              </div>
              <ChevronRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5" style={{ color: 'var(--text-muted)' }} />
            </div>

            {/* Metrics row */}
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <p className="text-[10px] uppercase font-medium tracking-wider" style={{ color: 'var(--text-muted)' }}>This Month</p>
                <p className="text-lg font-semibold mt-0.5" style={{ color: 'var(--text-primary)' }}>{v.monthTotal}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase font-medium tracking-wider" style={{ color: 'var(--text-muted)' }}>Routed</p>
                <p className="text-lg font-semibold mt-0.5" style={{ color: 'var(--text-primary)' }}>{v.routed}</p>
                {v.unmatched > 0 && (
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--danger-text)' }}>
                    {v.unmatched} unmatched
                  </p>
                )}
              </div>
              <div>
                <p className="text-[10px] uppercase font-medium tracking-wider" style={{ color: 'var(--text-muted)' }}>Conversion</p>
                <p className="text-lg font-semibold mt-0.5" style={{ color: v.conversionRate > 15 ? 'var(--accent)' : 'var(--text-primary)' }}>
                  {v.conversionRate}%
                </p>
              </div>
            </div>

            {/* Sparkline */}
            <div className="mb-4">
              <svg viewBox="0 0 100 30" className="w-full h-8" preserveAspectRatio="none">
                <defs>
                  <linearGradient id={`grad-${v.id}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--accent-glow)" />
                    <stop offset="100%" stopColor="transparent" />
                  </linearGradient>
                </defs>
                <polyline
                  fill={`url(#grad-${v.id})`}
                  stroke="var(--accent)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={`0,100 ${v.sparkPoints} 100,100`}
                />
                <polyline
                  fill="none"
                  stroke="var(--accent)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  points={v.sparkPoints}
                />
              </svg>
            </div>

            {/* Bottom chips */}
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                <MapPin size={10} /> {v.propertyCount} properties
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                <FileText size={10} /> {v.offerCount} offers
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                <Users size={10} /> {v.partnerCount} partners
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md border" style={{ borderColor: 'var(--border-separator)', color: 'var(--text-muted)' }}>
                <Activity size={10} /> {v.funnelCount} funnels
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
